'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import Captcha from 'react-google-recaptcha';
import { useWatch } from 'react-hook-form';
import { Button } from '~/components/Button';
import { Checkbox } from '~/components/Checkbox';
import { Heading } from '~/components/Heading';
import { Input } from '~/components/Input';
import { Box } from '~/components/Layout/Box';
import { MarketingOptOutField, PRIVACY_NOTICE_ID, PrivacyNotice, privacyPolicyApplies } from '~/components/MarketingConsentFields';
import { Notification } from '~/components/Notification';
import { Select } from '~/components/Select';
import { Textarea } from '~/components/TextArea';
import { useContactForm } from '~/hooks/useContactForm';
import { leadPassengerCountryOptions } from '~/schemas/contactFormSchema';
import { syncAutofilledInputs } from '~/utils/formHelper';
import { isSingleOptInEnabled } from '~/utils/singleOptInFlag';
import styles from './ContactForm.module.css';
const ContactForm = () => {
  const $ = _c(124);
  const t = useTranslations();
  const {
    Controller,
    control,
    getValues,
    handleSubmit,
    formState: t0,
    register,
    setValue,
    onSubmit,
    captchaRef,
    onCaptchaErrored,
    submitStatus
  } = useContactForm();
  const {
    errors,
    isSubmitting
  } = t0;
  const formRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["newsletterSignup", "marketingOptOut"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [newsletterSignup, marketingOptOut] = useWatch(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = isSingleOptInEnabled();
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const singleOptIn = t3;
  let t4;
  if ($[4] !== marketingOptOut || $[5] !== newsletterSignup) {
    t4 = privacyPolicyApplies({
      newsletterSignup,
      marketingOptOut
    });
    $[4] = marketingOptOut;
    $[5] = newsletterSignup;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const privacyApplies = t4;
  const outcomeRef = useRef(null);
  let t5;
  if ($[7] !== submitStatus?.state) {
    t5 = () => {
      if (submitStatus?.state === "error") {
        outcomeRef.current?.focus();
      }
    };
    $[7] = submitStatus?.state;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== submitStatus) {
    t6 = [submitStatus];
    $[9] = submitStatus;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== t) {
    t7 = t("content.contact_heading");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <Heading level="h2" variant="heading-lg">{t7}</Heading>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("content.contact_description");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <p>{t9}</p>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8) {
    t11 = <Box spaceBottom="--by-space-800">{t8}{t10}</Box>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== getValues || $[23] !== handleSubmit || $[24] !== onSubmit || $[25] !== setValue) {
    t12 = event => {
      syncAutofilledInputs(formRef.current, getValues, setValue);
      return handleSubmit(onSubmit)(event);
    };
    $[22] = getValues;
    $[23] = handleSubmit;
    $[24] = onSubmit;
    $[25] = setValue;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== captchaRef || $[28] !== onCaptchaErrored) {
    t13 = <Captcha ref={captchaRef} size="invisible" sitekey={process.env.NEXT_PUBLIC_RECAPTCHA ?? ""} onErrored={onCaptchaErrored} />;
    $[27] = captchaRef;
    $[28] = onCaptchaErrored;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== register) {
    t14 = register("middleName");
    $[30] = register;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14) {
    t15 = <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" placeholder="Leave this field empty" className={styles.honeypot} {...t14} />;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== errors.message?.message || $[35] !== t) {
    t16 = t17 => {
      const {
        field
      } = t17;
      return <Textarea {...field} id="message" name="message" label={t("forms.message_label")} helpText={t("content.forms.message_help")} rows={6} placeholder={t("content.forms.message_placeholder")} errorMessage={errors.message?.message} required={true} />;
    };
    $[34] = errors.message?.message;
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== Controller || $[38] !== control || $[39] !== t16) {
    t17 = <Controller name="message" control={control} render={t16} />;
    $[37] = Controller;
    $[38] = control;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== errors.firstName?.message || $[42] !== t) {
    t18 = t19 => {
      const {
        field: field_0
      } = t19;
      return <section className="w-1/2"><Input id="firstName" label={t("forms.name_first_label")} required={true} errorMessage={errors.firstName?.message} {...field_0} /></section>;
    };
    $[41] = errors.firstName?.message;
    $[42] = t;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== Controller || $[45] !== control || $[46] !== t18) {
    t19 = <Controller name="firstName" control={control} render={t18} />;
    $[44] = Controller;
    $[45] = control;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== errors.lastName?.message || $[49] !== t) {
    t20 = t21 => {
      const {
        field: field_1
      } = t21;
      return <section className="w-1/2"><Input id="lastName" label={t("forms.name_last_label")} required={true} errorMessage={errors.lastName?.message} {...field_1} /></section>;
    };
    $[48] = errors.lastName?.message;
    $[49] = t;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== Controller || $[52] !== control || $[53] !== t20) {
    t21 = <Controller name="lastName" control={control} render={t20} />;
    $[51] = Controller;
    $[52] = control;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t19 || $[56] !== t21) {
    t22 = <div className={styles.inlineFields}>{t19}{t21}</div>;
    $[55] = t19;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== errors.countryOfResidence?.message || $[59] !== t) {
    t23 = t24 => {
      const {
        field: field_2
      } = t24;
      return <Select id="countryOfResidence" name={field_2.name} label={t("content.forms.country_label")} selected={field_2.value} onChange={e => field_2.onChange(e.target.value)} onBlur={field_2.onBlur} errorMessage={errors.countryOfResidence?.message} required={true}><option value="" disabled={true}>{t("forms.country_placeholder")}</option>{leadPassengerCountryOptions.map(_temp)}</Select>;
    };
    $[58] = errors.countryOfResidence?.message;
    $[59] = t;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== Controller || $[62] !== control || $[63] !== t23) {
    t24 = <Controller name="countryOfResidence" control={control} render={t23} />;
    $[61] = Controller;
    $[62] = control;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== errors.email?.message || $[66] !== t) {
    t25 = t26 => {
      const {
        field: t27
      } = t26;
      const {
        value,
        onChange
      } = t27;
      return <section className="w-1/2"><Input id="email" name="email" value={value} onChange={onChange} label={t("forms.email_label")} type="email" errorMessage={errors.email?.message} required={true} /></section>;
    };
    $[65] = errors.email?.message;
    $[66] = t;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== Controller || $[69] !== control || $[70] !== t25) {
    t26 = <Controller name="email" control={control} render={t25} />;
    $[68] = Controller;
    $[69] = control;
    $[70] = t25;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t) {
    t27 = t28 => {
      const {
        field: t29
      } = t28;
      const {
        value: value_0,
        onChange: onChange_0
      } = t29;
      return <section className="w-1/2"><Input id="phone" name="phone" value={value_0} onChange={onChange_0} label={t("forms.phone_label")} type="tel" /></section>;
    };
    $[72] = t;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== Controller || $[75] !== control || $[76] !== t27) {
    t28 = <Controller name="phone" control={control} render={t27} />;
    $[74] = Controller;
    $[75] = control;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== t26 || $[79] !== t28) {
    t29 = <div className={styles.inlineFields}>{t26}{t28}</div>;
    $[78] = t26;
    $[79] = t28;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t) {
    t30 = t31 => {
      const {
        field: field_3
      } = t31;
      return <Input id="whereDidYouHearAboutUs" label={t("content.forms.referral_label")} placeholder={t("content.forms.referral_placeholder")} {...field_3} />;
    };
    $[81] = t;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== Controller || $[84] !== control || $[85] !== t30) {
    t31 = <Controller name="whereDidYouHearAboutUs" control={control} render={t30} />;
    $[83] = Controller;
    $[84] = control;
    $[85] = t30;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== Controller || $[88] !== control || $[89] !== t) {
    t32 = !singleOptIn && <Controller name="newsletterSignup" control={control} render={t33 => {
      const {
        field: field_4
      } = t33;
      return <Checkbox id="newsletterSignup" name={field_4.name} checked={field_4.value} onChange={e_0 => field_4.onChange(e_0.target.checked)} label={t("content.forms.newsletter_signup")} />;
    }} />;
    $[87] = Controller;
    $[88] = control;
    $[89] = t;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = singleOptIn && <PrivacyNotice />;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] !== control) {
    t34 = <MarketingOptOutField control={control} />;
    $[92] = control;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  let t35;
  if ($[94] !== privacyApplies) {
    t35 = !singleOptIn && privacyApplies ? <PrivacyNotice /> : null;
    $[94] = privacyApplies;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  const t36 = submitStatus?.state;
  const t37 = submitStatus?.message;
  let t38;
  if ($[96] !== t36 || $[97] !== t37) {
    t38 = <div ref={outcomeRef} tabIndex={-1}><Notification state={t36} message={t37} /></div>;
    $[96] = t36;
    $[97] = t37;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  const t39 = singleOptIn || privacyApplies ? PRIVACY_NOTICE_ID : undefined;
  let t40;
  if ($[99] !== t) {
    t40 = t("forms.submit");
    $[99] = t;
    $[100] = t40;
  } else {
    t40 = $[100];
  }
  let t41;
  if ($[101] !== t40) {
    t41 = <span>{t40}</span>;
    $[101] = t40;
    $[102] = t41;
  } else {
    t41 = $[102];
  }
  let t42;
  if ($[103] !== isSubmitting || $[104] !== t39 || $[105] !== t41) {
    t42 = <Button data-ua-click="contact_form_submit" type="submit" size="fullWidth" isLoading={isSubmitting} aria-describedby={t39}>{t41}</Button>;
    $[103] = isSubmitting;
    $[104] = t39;
    $[105] = t41;
    $[106] = t42;
  } else {
    t42 = $[106];
  }
  let t43;
  if ($[107] !== t12 || $[108] !== t13 || $[109] !== t15 || $[110] !== t17 || $[111] !== t22 || $[112] !== t24 || $[113] !== t29 || $[114] !== t31 || $[115] !== t32 || $[116] !== t34 || $[117] !== t35 || $[118] !== t38 || $[119] !== t42) {
    t43 = <Box isFlex={true}><form ref={formRef} onSubmit={t12} noValidate={true} className={styles.form}>{t13}{t15}{t17}{t22}{t24}{t29}{t31}{t32}{t33}{t34}{t35}{t38}{t42}</form></Box>;
    $[107] = t12;
    $[108] = t13;
    $[109] = t15;
    $[110] = t17;
    $[111] = t22;
    $[112] = t24;
    $[113] = t29;
    $[114] = t31;
    $[115] = t32;
    $[116] = t34;
    $[117] = t35;
    $[118] = t38;
    $[119] = t42;
    $[120] = t43;
  } else {
    t43 = $[120];
  }
  let t44;
  if ($[121] !== t11 || $[122] !== t43) {
    t44 = <Box isContainer={true}><Box spaceHorizontal="--by-space-400">{t11}{t43}</Box></Box>;
    $[121] = t11;
    $[122] = t43;
    $[123] = t44;
  } else {
    t44 = $[123];
  }
  return t44;
};
export { ContactForm };
function _temp(option) {
  return <option key={option} value={option}>{option}</option>;
}